<!-- src/views/DashboardView.vue -->

<template>
  <div class="my-courses-page">
    <!-- Hero Section with Actions -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="hero-icon">
          <i class="fas fa-graduation-cap"></i>
        </div>
        <h1 class="hero-title">{{ $t("menu.my_courses") }}</h1>
        <p class="hero-subtitle">
          {{ $t("dashboard.subtitle") || "استمر في رحلة التعلم الخاصة بك" }}
        </p>
      </div>
      
      <!-- Action Buttons -->
      <div class="hero-actions">
        <template v-if="isReorderMode">
          <button class="action-btn action-btn--cancel" @click="cancelReorder">
            <i class="fas fa-times"></i>
            <span>إلغاء</span>
          </button>
          <button 
            class="action-btn action-btn--save" 
            @click="saveReorder"
            :disabled="isSavingOrder"
          >
            <i class="fas fa-check" v-if="!isSavingOrder"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            <span>حفظ الترتيب</span>
          </button>
        </template>
        <template v-else>
          <button class="reorder-btn" @click="enterReorderMode">
            <i class="fas fa-sort"></i>
            <span>{{ $t('dashboard.reorder_courses') || 'إعادة الترتيب' }}</span>
          </button>
        </template>
      </div>
      
      <div class="hero-decoration">
        <div class="circle circle-1"></div>
        <div class="circle circle-2"></div>
        <div class="circle circle-3"></div>
      </div>
    </div>

    <!-- Tabs Navigation -->
    <div class="tabs-container">
      <div class="tabs-wrapper">
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          class="tab-btn"
          :class="{ active: activeTab === index }"
          @click="switchTab(index)"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="courses-container">
      <!-- Loading State -->
      <div v-if="isLoading && activeTab === 0" class="loading-state">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">{{ $t("common.loading") }}</p>
      </div>

      <!-- Not Logged In State -->
      <div v-else-if="!isLoggedIn" class="login-prompt">
        <div class="login-card">
          <div class="login-icon">
            <i class="fas fa-user-lock"></i>
          </div>
          <h2>{{ $t("common.please_login_to_continue") }}</h2>
          <p class="login-desc">{{ $t("dashboard.login_desc") }}</p>
          <a href="/login" class="login-btn">
            <i class="fas fa-sign-in-alt"></i>
            <span>{{ $t("common.login") }}</span>
          </a>
        </div>
      </div>

      <!-- Tab Content -->
      <div v-else class="tab-content">
        <!-- My Courses Tab -->
        <div v-show="activeTab === 0" class="courses-content">
        <!-- Reorder Mode Loading -->
        <div v-if="isReorderLoading" class="loading-state">
          <div class="loading-spinner">
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
          </div>
          <p class="loading-text">{{ $t("dashboard.loading_courses") || "جاري تحميل الدورات..." }}</p>
        </div>
        
        <!-- Stats Bar -->
        <div class="stats-bar" v-else-if="!isReorderMode && courses.length > 0">
          <div class="stat-item">
            <div class="stat-icon">
              <i class="fas fa-book-open"></i>
            </div>
            <div class="stat-info">
              <span class="stat-number">{{ courses.length }}</span>
              <span class="stat-label">{{
                $t("dashboard.total_courses") || "إجمالي الدورات"
              }}</span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="courses.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-folder-open"></i>
          </div>
          <h3>{{ $t("dashboard.no_courses") || "لا توجد دورات بعد" }}</h3>
          <p>
            {{
              $t("dashboard.no_courses_desc") ||
              "ابدأ رحلتك التعليمية واشترك في دورة جديدة"
            }}
          </p>
          <a href="/courses" class="browse-btn">
            <i class="fas fa-search"></i>
            <span>{{ $t("dashboard.browse_courses") || "تصفح الدورات" }}</span>
          </a>
        </div>

        <!-- Reorder Mode Grid -->
        <div v-if="isReorderMode && !isReorderLoading" class="courses-grid reorder-grid">
          <div
            v-for="(course, index) in reorderCourses"
            :key="'reorder-' + course.id"
            class="course-card reorder-card"
            draggable="true"
            @dragstart="onDragStart($event, index)"
            @dragover.prevent="onDragOver($event, index)"
            @drop="onDrop($event, index)"
            @dragend="onDragEnd"
          >
            <div class="reorder-handle">
              <i class="fas fa-grip-vertical"></i>
            </div>
            <div class="card-image-wrapper">
              <img :src="getCourseImage(course)" :alt="course.title" class="card-image" />
              <div class="card-badge" v-if="course.progress">
                <span>{{ course.progress }}%</span>
              </div>
            </div>
            <div class="card-content">
              <h3 class="card-title">{{ course.title }}</h3>
              <div class="card-meta">
                <div class="meta-item" v-if="course.institute">
                  <i class="fas fa-university"></i>
                  <span>{{ course.institute.title }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Normal Courses Grid -->
        <div v-else-if="!isReorderMode && !isReorderLoading" class="courses-grid">
          <div
            v-for="(course, index) in courses"
            :key="course.id"
            class="course-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="card-image-wrapper">
              <img :src="getCourseImage(course)" :alt="course.title" class="card-image" />
              <div class="card-overlay">
                <button @click="showCourse(course)" class="play-btn">
                  <i class="fas fa-play"></i>
                </button>
              </div>
              <div class="card-badge" v-if="course.progress">
                <span>{{ course.progress }}%</span>
              </div>
            </div>
            <div class="card-content">
              <h3 class="card-title">{{ course.title }}</h3>

              <!-- Course Meta Info -->
              <div class="card-meta">
                <div class="meta-item" v-if="course.institute">
                  <i class="fas fa-university"></i>
                  <span>{{ course.institute.title }}</span>
                </div>
                <div class="meta-item meta-update" v-if="course.last_update || course.lastUpdate">
                  <i class="fas fa-calendar-alt"></i>
                  <div class="update-info">
                    <span class="update-label">آخر تحديث:</span>
                    <span class="update-date">{{
                      formatDate(course.last_update || course.lastUpdate)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Progress Bar -->
              <div class="progress-section">
                <div class="progress-info">
                  <div class="progress-text">
                    <i class="fas fa-chart-line"></i>
                    <span class="progress-title">{{
                      $t("dashboard.course_progress") || "تقدم الدورة"
                    }}</span>
                  </div>
                  <span class="progress-percentage"
                    >{{ course.progress || 0 }}%</span
                  >
                </div>
                <div class="progress-bar-wrapper">
                  <div
                    class="progress-bar-fill"
                    :style="{ width: (course.progress || 0) + '%' }"
                  >
                    <span class="progress-bar-text" v-if="course.progress > 5"
                      >{{ course.progress }}%</span
                    >
                  </div>
                </div>
                <div class="progress-details">
                  <span class="progress-status">{{
                    getProgressStatus(course.progress || 0)
                  }}</span>
                </div>
              </div>

              <div class="card-instructor">
                <div class="instructor-avatar">
                  <i class="fas fa-user"></i>
                </div>
                <span class="instructor-name"
                  >{{ getInstructorName(course) }}</span
                >
              </div>

              <div class="card-footer">
                <button @click="showCourse(course)" class="view-btn">
                  <span>{{ $t("common.view") }}</span>
                  <i class="fas fa-arrow-left"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- Live Streams Tab -->
        <div v-show="activeTab === 1" class="live-streams-content">
          <!-- Loading State for Live -->
          <div v-if="isLoadingLive" class="loading-state">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">{{ $t("common.loading") }}</p>
          </div>

          <!-- Empty State for Live -->
          <div v-else-if="!hasLiveStreams" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-broadcast-tower"></i>
            </div>
            <h3>{{ $t("dashboard.no_live_streams") || "لا توجد بثوث مباشرة" }}</h3>
            <p>{{ $t("dashboard.no_live_streams_desc") || "لا توجد بثوث مباشرة حالياً" }}</p>
          </div>

          <!-- Live Streams Content -->
          <div v-else class="live-streams-grid">
            <!-- New Live Streams Section -->
            <div v-if="liveClasses.newClasses && liveClasses.newClasses.length > 0" class="live-section">
              <h3 class="section-title">
                <i class="fas fa-circle live-indicator"></i>
                {{ $t("dashboard.new_live_streams") || "البثوث المباشرة الجديدة" }}
              </h3>
              <div class="live-cards">
                <div
                  v-for="liveClass in liveClasses.newClasses"
                  :key="liveClass.id"
                  class="live-card"
                  @click="joinLiveStream(liveClass)"
                >
                  <div class="live-badge">
                    <span>{{ $t("dashboard.live") || "مباشر" }}</span>
                  </div>
                  <h4 class="live-title">{{ liveClass.title }}</h4>
                  <p class="live-course">{{ liveClass.course_id }}</p>
                  <p class="live-status" :class="getLiveStatusClass(liveClass)">
                    {{ getLiveStatus(liveClass) }}
                  </p>
                  <button class="join-btn" :disabled="!canJoinLive(liveClass)">
                    <i class="fas fa-play"></i>
                    <span>{{ $t("dashboard.join") || "انضمام" }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Upcoming Live Streams Section -->
            <div v-if="liveClasses.oldClasses && liveClasses.oldClasses.length > 0" class="live-section">
              <h3 class="section-title">
                <i class="fas fa-clock"></i>
                {{ $t("dashboard.upcoming_live_streams") || "البثوث القادمة" }}
              </h3>
              <div class="live-cards">
                <div
                  v-for="liveClass in liveClasses.oldClasses"
                  :key="liveClass.id"
                  class="live-card upcoming"
                  @click="joinLiveStream(liveClass)"
                >
                  <div class="live-badge upcoming-badge">
                    <span>{{ $t("dashboard.upcoming") || "قادم" }}</span>
                  </div>
                  <h4 class="live-title">{{ liveClass.title }}</h4>
                  <p class="live-course">{{ liveClass.course_id }}</p>
                  <p class="live-status">
                    {{ getLiveStatus(liveClass) }}
                  </p>
                  <button class="join-btn" :disabled="!canJoinLive(liveClass)">
                    <i class="fas fa-play"></i>
                    <span>{{ $t("dashboard.join") || "انضمام" }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Test Me (QBank) Tab -->
        <div v-show="activeTab === 2" class="qbank-content">
          <!-- Loading State -->
          <div v-if="isLoadingQbank" class="loading-state">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="loading-text">{{ $t("common.loading") }}</p>
          </div>

          <!-- Not Logged In State -->
          <div v-else-if="!isLoggedIn" class="login-prompt">
            <div class="login-card">
              <div class="login-icon">
                <i class="fas fa-user-lock"></i>
              </div>
              <h2>{{ $t("common.please_login_to_continue") }}</h2>
              <p class="login-desc">{{ $t("dashboard.login_desc") }}</p>
              <a href="/login" class="login-btn">
                <i class="fas fa-sign-in-alt"></i>
                <span>{{ $t("common.login") }}</span>
              </a>
            </div>
          </div>

          <!-- QBank Subscriptions -->
          <div v-else class="qbank-subscriptions">
            <!-- Filter Section -->
            <div class="filter-section">
              <div class="filter-tabs">
                <button
                  class="filter-tab"
                  :class="{ active: qbankFilter === 'all' }"
                  @click="qbankFilter = 'all'"
                >
                  <i class="fas fa-th-large"></i>
                  <span>{{ $t("common.all") || "الكل" }}</span>
                </button>
                <button
                  class="filter-tab"
                  :class="{ active: qbankFilter === 'active' }"
                  @click="qbankFilter = 'active'"
                >
                  <i class="fas fa-check-circle"></i>
                  <span>{{ $t("qbank.active") || "نشط" }}</span>
                </button>
                <button
                  class="filter-tab"
                  :class="{ active: qbankFilter === 'expired' }"
                  @click="qbankFilter = 'expired'"
                >
                  <i class="fas fa-times-circle"></i>
                  <span>{{ $t("qbank.expired") || "منتهي" }}</span>
                </button>
              </div>
            </div>

            <!-- Subscriptions List -->
            <SubscriptionsList :subscriptions="filteredQbankSubscriptions" />

            <!-- New Subscription Button -->
            <div class="new-subscription-section">
              <a href="/qbank-plan" class="new-subscription-btn">
                <i class="fas fa-plus-circle"></i>
                <span>{{ $t("qbank.new_subscription") || "اشتراك جديد" }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  apiUrl,
  courseImagePath,
  deviceId,
  getBrowserName,
  api_get,
  api,
  instructorImagePath,
} from "@/utils/api";
import Swal from "sweetalert2";
import { getSwalConfig } from "@/utils/swal-config";

import SubscriptionsList from "@/views/Qbank/Home/SubscriptionsList.vue";

export default {
  name: "Dashboard",
  components: {
    SubscriptionsList,
  },
  data() {
    return {
      token: null,
      respo: null,
      isLoggedIn: false,
      isLoading: true,
      courses: [],
      activeTab: 0,
      isLoadingLive: false,
      liveClasses: {
        newClasses: [],
        oldClasses: [],
      },
      isReorderMode: false,
      isReorderLoading: false,
      isSavingOrder: false,
      reorderCourses: [],
      draggedIndex: null,
      liveTimer: null,
      // QBank subscriptions data
      isLoadingQbank: false,
      qbankSubscriptions: [],
      qbankFilter: "all",
    };
  },
  computed: {
    tabs() {
      return [
        {
          label: this.$t("dashboard.my_courses_tab") || "دوراتي",
          icon: "fas fa-book",
        },
        {
          label: this.$t("dashboard.live_tab") || "مباشر",
          icon: "fas fa-broadcast-tower",
        },
        {
          label: this.$t("dashboard.test_me_tab") || "اختبر نفسك",
          icon: "fas fa-question-circle",
        },
      ];
    },
    hasLiveStreams() {
      return (
        (this.liveClasses.newClasses && this.liveClasses.newClasses.length > 0) ||
        (this.liveClasses.oldClasses && this.liveClasses.oldClasses.length > 0)
      );
    },
    filteredQbankSubscriptions() {
      if (this.qbankFilter === "all") {
        return this.qbankSubscriptions;
      }
      return this.qbankSubscriptions.filter((sub) => {
        const isValid = sub.is_valid === 1 || sub.is_valid === true;
        if (this.qbankFilter === "active") return isValid;
        if (this.qbankFilter === "expired") return !isValid;
        return true;
      });
    },
  },
  watch: {
    activeTab(newTab, oldTab) {
      if (newTab === 1) {
        if (!this.hasLiveStreams && !this.isLoadingLive) {
          this.getLiveClasses();
        }
        this.startLiveTimer();
      } else if (oldTab === 1) {
        this.stopLiveTimer();
      }
      // Load QBank subscriptions when Test Me tab is selected
      if (newTab === 2 && this.qbankSubscriptions.length === 0 && !this.isLoadingQbank) {
        this.getQbankSubscriptions();
      }
    },
  },
  beforeUnmount() {
    this.stopLiveTimer();
  },
  created() {
    this.token = localStorage.getItem("access_token");
    this.respo = localStorage.getItem("respo");
    this.getUserCourses();
  },
  mounted() {
    if (this.activeTab === 1) {
      this.startLiveTimer();
    }
  },
  methods: {
    getUserCourses() {
      if (!this.token) {
        this.isLoading = false;
        this.isLoggedIn = false;
        return;
      }
      let headers = {
        Authorization: `Bearer ${this.token}`,
      };
      api_get("/subscriptions/courses", headers)
        .then(async (response) => {
          this.isLoading = false;
          if (response.redirected || !response.ok) {
            this.isLoggedIn = false;
            return;
          }
          this.isLoggedIn = true;
          let res = await response.json();
          this.courses = res.data || res.courses || [];
          console.log("Courses data:", this.courses);
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
          this.isLoggedIn = false;
        });
    },
    async getLiveClasses() {
      if (!this.token) return;
      
      this.isLoadingLive = true;
      try {
        const response = await fetch(`${apiUrl}/livestream/get-subscribed-live-classes`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          this.liveClasses = {
            newClasses: data.new_classes || [],
            oldClasses: data.old_classes || [],
          };
        }
      } catch (error) {
        console.error("Error fetching live classes:", error);
      } finally {
        this.isLoadingLive = false;
      }
    },
    async joinLiveStream(liveClass) {
      if (!this.canJoinLive(liveClass)) {
        alert(this.$t("dashboard.broadcast_unavailable") || "البث غير متاح حالياً");
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/livestream/token/${liveClass.id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (data.token) {
          window.open(`https://live.academia.ly/?token=${data.token}`, "_blank");
        } else {
          alert(this.$t("dashboard.failed_to_get_token") || "فشل في جلب التوكن");
        }
      } catch (error) {
        console.error("Error joining live stream:", error);
        alert(this.$t("common.error") || "حدث خطأ");
      }
    },
    canJoinLive(liveClass) {
      const now = new Date();
      const start = new Date(liveClass.live_starts_at);
      const end = new Date(liveClass.live_ends_at);
      return now >= start && now <= end;
    },
    getLiveStatus(liveClass) {
      const now = new Date();
      const start = new Date(liveClass.live_starts_at);
      const end = new Date(liveClass.live_ends_at);

      if (now < start) {
        const diff = start - now;
        return (this.$t("dashboard.starts_in") || "يبدأ في") + " " + this.formatDuration(diff);
      } else if (now > end) {
        return this.$t("dashboard.stream_ended") || "انتهى البث";
      } else {
        const remaining = end - now;
        return (this.$t("dashboard.ends_in") || "ينتهي في") + " " + this.formatDuration(remaining);
      }
    },
    getLiveStatusClass(liveClass) {
      const now = new Date();
      const start = new Date(liveClass.live_starts_at);
      const end = new Date(liveClass.live_ends_at);

      if (now >= start && now <= end) return "status-live";
      if (now < start) return "status-upcoming";
      return "status-ended";
    },
    async getQbankSubscriptions() {
      if (!this.token) {
        this.isLoggedIn = false;
        this.isLoadingQbank = false;
        return;
      }
      
      this.isLoadingQbank = true;
      try {
        const response = await fetch(
          `${apiUrl}/profile/qbanks/subscription-plans`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status === 302 || response.redirected || !response.ok) {
          this.isLoggedIn = false;
          this.isLoadingQbank = false;
          return;
        }
        this.isLoggedIn = true;
        const data = await response.json();
        this.qbankSubscriptions = data.data || data.subscriptions || [];
      } catch (error) {
        console.error("Error fetching QBank subscriptions:", error);
        this.qbankSubscriptions = [];
      } finally {
        this.isLoadingQbank = false;
      }
    },
    formatDuration(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (days > 0) {
        return `${days} ${this.$t("dashboard.days") || "يوم"}, ${hours} ${this.$t("dashboard.hours") || "ساعة"}`;
      } else if (hours > 0) {
        return `${hours} ${this.$t("dashboard.hours") || "ساعة"}, ${minutes} ${this.$t("dashboard.minutes") || "دقيقة"}`;
      } else {
        return `${minutes} ${this.$t("dashboard.minutes") || "دقيقة"}`;
      }
    },
    showCourse(course) {
      window.location.href = "course-details?id=" + course.id;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}/${month}/${day} - ${hours}:${minutes}`;
    },
    getProgressStatus(progress) {
      if (progress === 0) return "";
      if (progress < 25) return "في البداية";
      if (progress < 50) return "في المنتصف";
      if (progress < 75) return "متقدم";
      if (progress < 100) return "على وشك الانتهاء";
      return "مكتملة";
    },
    getCourseImage(course) {
      // Support both mobile endpoint (preview_image/previewImage) and desktop endpoint (image)
      const imagePath = course.preview_image || course.previewImage || course.image;
      if (!imagePath) return "";
      // If it's already a full URL, return as is
      if (imagePath.startsWith("http")) return imagePath;
      // Otherwise, prepend the course image path
      return courseImagePath + imagePath;
    },
    getInstructorName(course) {
      // Support both mobile endpoint (instructor_name/instructorName) and desktop endpoint (user object)
      if (course.instructor_name) return course.instructor_name;
      if (course.instructorName) return course.instructorName;
      if (course.user) {
        return `${course.user.fname || ""} ${course.user.lname || ""}`.trim();
      }
      return "";
    },
    logout() {
      api(
        "/logout",
        "POST",
        {},
        { Authorization: `Bearer ${this.token}` },
        false
      ).then(async (response) => {
        localStorage.removeItem("access_token");
      });
      localStorage.removeItem("access_token");
    },
    switchTab(index) {
      if (this.isReorderMode) {
        this.cancelReorder();
      }
      this.activeTab = index;
    },
    async enterReorderMode() {
      this.isReorderMode = true;
      this.isReorderLoading = true;
      this.reorderCourses = [];
      
      try {
        let allCourses = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore) {
          const response = await fetch(`${apiUrl}/subscriptions/courses?page=${page}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            const courses = data.data || [];
            allCourses = allCourses.concat(courses);
            
            // Check pagination meta
            if (data.meta) {
              hasMore = data.meta.current_page < data.meta.last_page;
              page++;
            } else if (courses.length >= 15) {
              hasMore = true;
              page++;
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }
        
        this.reorderCourses = allCourses;
      } catch (error) {
        console.error("Error loading courses for reorder:", error);
        alert(this.$t("common.error") || "حدث خطأ");
        this.isReorderMode = false;
      } finally {
        this.isReorderLoading = false;
      }
    },
    cancelReorder() {
      this.isReorderMode = false;
      this.reorderCourses = [];
      this.draggedIndex = null;
    },
    async saveReorder() {
      if (this.isSavingOrder || this.reorderCourses.length === 0) return;
      
      this.isSavingOrder = true;
      
      try {
        const body = {
          courses: this.reorderCourses.map((course, index) => ({
            order_id: course.id,
            position: index,
          })),
        };
        
        const response = await fetch(`${apiUrl}/subscriptions/courses/reorder`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
        
        const data = await response.json();
        
        if (response.ok) {
          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("dashboard.order_saved") || "تم حفظ الترتيب",
            text: data.message || "تم تحديث ترتيب الدورات بنجاح",
            confirmButtonText: this.$t("common.ok") || "حسناً",
            timer: 2000,
            timerProgressBar: true,
          });
          this.getUserCourses();
          this.cancelReorder();
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title: this.$t("common.error") || "خطأ",
            text: data.message || "حدث خطأ أثناء حفظ الترتيب",
            confirmButtonText: this.$t("common.ok") || "حسناً",
          });
        }
      } catch (error) {
        console.error("Error saving order:", error);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("common.error") || "خطأ",
          text: this.$t("common.network_error") || "حدث خطأ في الاتصال",
          confirmButtonText: this.$t("common.ok") || "حسناً",
        });
      } finally {
        this.isSavingOrder = false;
      }
    },
    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.target.classList.add("dragging");
    },
    onDragOver(event, index) {
      event.preventDefault();
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      
      const draggedItem = this.reorderCourses[this.draggedIndex];
      const newCourses = [...this.reorderCourses];
      newCourses.splice(this.draggedIndex, 1);
      newCourses.splice(index, 0, draggedItem);
      this.reorderCourses = newCourses;
      this.draggedIndex = index;
    },
    onDrop(event, index) {
      event.preventDefault();
      event.target.classList.remove("drag-over");
    },
    onDragEnd(event) {
      event.target.classList.remove("dragging");
      this.draggedIndex = null;
    },
    startLiveTimer() {
      this.stopLiveTimer();
      this.liveTimer = setInterval(() => {
        this.$forceUpdate();
      }, 1000);
    },
    stopLiveTimer() {
      if (this.liveTimer) {
        clearInterval(this.liveTimer);
        this.liveTimer = null;
      }
    },
  },
};
</script>

<style scoped>
/* ===== Main Page Styles ===== */
.my-courses-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
  direction: rtl;
}

/* ===== Hero Section ===== */
.hero-section {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  padding: 60px 20px;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 50px 50px;
  margin-bottom: 40px;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.3);
}

/* ===== Hero Actions ===== */
.hero-actions {
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  gap: 10px;
  z-index: 10;
}

.action-btn {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.action-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn--cancel {
  background: rgba(220, 53, 69, 0.9);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 25px;
  width: auto;
}

.action-btn--cancel span {
  font-size: 14px;
  font-weight: 600;
  color: white !important;
}

.action-btn--cancel i {
  color: white !important;
}

.action-btn--cancel:hover {
  background: rgba(220, 53, 69, 1);
}

.action-btn--save {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 25px;
  min-width: auto;
  width: auto;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.action-btn--save span {
  font-size: 14px;
  font-weight: 600;
  color: white !important;
}

.action-btn--save i {
  color: white !important;
}

.action-btn--save:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
}

/* Reorder Button - Premium Design */
.reorder-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 30px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.reorder-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.reorder-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.reorder-btn:hover::before {
  opacity: 1;
}

.reorder-btn:active {
  transform: translateY(0);
}

.reorder-btn i {
  font-size: 18px;
  transition: transform 0.3s ease;
  color: white !important;
}

.reorder-btn span {
  color: white !important;
}

.reorder-btn:hover i {
  transform: rotate(180deg);
}

.hero-content {
  text-align: center;
  position: relative;
  z-index: 2;
}

.hero-icon {
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  backdrop-filter: blur(10px);
  animation: float 3s ease-in-out infinite;
}

.hero-icon i {
  font-size: 45px;
  color: white;
}

.hero-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  font-weight: 400;
}

.hero-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -50px;
  animation: pulse 4s ease-in-out infinite;
}

.circle-2 {
  width: 150px;
  height: 150px;
  bottom: -30px;
  left: 10%;
  animation: pulse 5s ease-in-out infinite 1s;
}

.circle-3 {
  width: 100px;
  height: 100px;
  top: 30%;
  left: 5%;
  animation: pulse 6s ease-in-out infinite 2s;
}

/* ===== Courses Container ===== */
.courses-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px 60px;
}

/* ===== Loading State ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00a83b;
  animation-duration: 1s;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #008f32;
  animation-duration: 0.8s;
}

.loading-text {
  margin-top: 20px;
  color: #00c744;
  font-size: 1.1rem;
  font-weight: 600;
}

/* ===== Login Prompt ===== */
.login-prompt {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
}

.login-card {
  background: white;
  border-radius: 30px;
  padding: 60px 50px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  max-width: 450px;
  width: 100%;
}

.login-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.login-icon i {
  font-size: 40px;
  color: white;
}

.login-card h2 {
  color: #2d3748;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.login-desc {
  color: #718096;
  font-size: 1rem;
  margin-bottom: 30px;
}

.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 16px 40px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.login-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.4);
  color: white;
}

/* ===== Stats Bar ===== */
.stats-bar {
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.stat-item {
  background: white;
  border-radius: 20px;
  padding: 25px 35px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 200px;
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px rgba(0, 199, 68, 0.15);
}

.stat-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 24px;
  color: #00c744;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 2rem;
  font-weight: 800;
  color: #00c744;
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: #718096;
  margin-top: 5px;
}

/* ===== Empty State ===== */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
}

.empty-icon i {
  font-size: 50px;
  color: #00c744;
}

.empty-state h3 {
  color: #2d3748;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.empty-state p {
  color: #718096;
  font-size: 1rem;
  margin-bottom: 30px;
}

.browse-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 14px 35px;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.browse-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.4);
  color: white;
}

/* ===== Courses Grid ===== */
.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
}

/* ===== Course Card ===== */
.course-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  position: relative;
}

/* ===== Reorder Mode Styles ===== */
.reorder-grid {
  user-select: none;
}

.reorder-card {
  cursor: move;
  opacity: 1;
  animation: none;
}

.reorder-card.dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.reorder-handle {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  background: rgba(0, 199, 68, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  z-index: 5;
  cursor: grab;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}

.reorder-handle:active {
  cursor: grabbing;
}

.reorder-card .card-overlay {
  display: none;
}

.course-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 60px rgba(0, 199, 68, 0.15);
}

.card-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.course-card:hover .card-image {
  transform: scale(1.1);
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.course-card:hover .card-overlay {
  opacity: 1;
}

.play-btn {
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.play-btn i {
  font-size: 24px;
  color: #00c744;
  margin-right: -3px;
}

.play-btn:hover {
  transform: scale(1.1);
  background: #00c744;
}

.play-btn:hover i {
  color: white;
}

.card-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.3);
}

.card-content {
  padding: 25px;
}

.card-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e2e8f0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
}

.meta-item i {
  font-size: 0.85rem;
  color: #00c744;
  width: 16px;
}

.meta-update {
  background: linear-gradient(135deg, #f0fdf4 0%, #e8fff1 100%);
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid #d4f7e4;
}

.update-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.update-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.update-date {
  font-size: 0.85rem;
  color: #2d3748;
  font-weight: 600;
}

.progress-section {
  margin-bottom: 15px;
  padding: 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #e8fff1 100%);
  border-radius: 12px;
  border: 1px solid #d4f7e4;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-text i {
  color: #00c744;
  font-size: 0.9rem;
}

.progress-title {
  font-size: 0.9rem;
  color: #2d3748;
  font-weight: 600;
}

.progress-percentage {
  font-size: 1.1rem;
  color: #00c744;
  font-weight: 800;
  background: white;
  padding: 4px 12px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 199, 68, 0.15);
}

.progress-bar-wrapper {
  width: 100%;
  height: 12px;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c744 0%, #00a83b 100%);
  border-radius: 10px;
  transition: width 0.6s ease;
  box-shadow: 0 2px 8px rgba(0, 199, 68, 0.3);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 8px;
  position: relative;
}

.progress-bar-text {
  font-size: 0.7rem;
  color: white;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.progress-details {
  display: flex;
  justify-content: center;
}

.progress-status {
  font-size: 0.8rem;
  color: #00a83b;
  font-weight: 600;
  background: white;
  padding: 3px 10px;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 199, 68, 0.1);
}

.card-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-instructor {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.instructor-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.instructor-avatar i {
  font-size: 16px;
  color: #00c744;
}

.instructor-name {
  font-size: 0.95rem;
  color: #718096;
  font-weight: 500;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
}

.view-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 12px 28px;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.25);
}

.view-btn:hover {
  transform: translateX(-5px);
  box-shadow: 0 12px 35px rgba(0, 199, 68, 0.35);
}

.view-btn i {
  transition: transform 0.3s ease;
  color: white;
}

.view-btn:hover i {
  transform: translateX(-5px);
}

/* ===== Dark Mode Support ===== */
[data-bs-theme="dark"] .my-courses-page,
[data-theme="dark"] .my-courses-page,
.dark .my-courses-page {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

[data-bs-theme="dark"] .login-card,
[data-theme="dark"] .login-card,
.dark .login-card {
  background: #1e293b;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .login-card h2,
[data-theme="dark"] .login-card h2,
.dark .login-card h2 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .login-desc,
[data-theme="dark"] .login-desc,
.dark .login-desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .stat-item,
[data-theme="dark"] .stat-item,
.dark .stat-item {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .stat-icon,
[data-theme="dark"] .stat-icon,
.dark .stat-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .stat-label,
[data-theme="dark"] .stat-label,
.dark .stat-label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .empty-state,
[data-theme="dark"] .empty-state,
.dark .empty-state {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .empty-state h3,
[data-theme="dark"] .empty-state h3,
.dark .empty-state h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-state p,
[data-theme="dark"] .empty-state p,
.dark .empty-state p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .course-card,
[data-theme="dark"] .course-card,
.dark .course-card {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .card-title,
[data-theme="dark"] .card-title,
.dark .card-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .instructor-avatar,
[data-theme="dark"] .instructor-avatar,
.dark .instructor-avatar {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .instructor-name,
[data-theme="dark"] .instructor-name,
.dark .instructor-name {
  color: #94a3b8;
}

[data-bs-theme="dark"] .loading-text,
[data-theme="dark"] .loading-text,
.dark .loading-text {
  color: #4ade80;
}

/* ===== Animations ===== */
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Tabs Navigation ===== */
.tabs-container {
  max-width: 1400px;
  margin: -20px auto 30px;
  padding: 0 20px;
  position: relative;
  z-index: 10;
}

.tabs-wrapper {
  display: flex;
  gap: 10px;
  background: white;
  padding: 8px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  justify-content: center;
  flex-wrap: wrap;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  border: none;
  background: transparent;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: #718096;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn:hover {
  background: #f0fdf4;
  color: #00c744;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white !important;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

.tab-btn.active i,
.tab-btn.active span {
  color: white !important;
}

.tab-btn i {
  font-size: 1.1rem;
}

/* ===== Tab Content ===== */
.tab-content {
  animation: fadeInUp 0.4s ease;
}

/* ===== Live Streams Styles ===== */
.live-streams-content {
  animation: fadeInUp 0.4s ease;
}

.live-section {
  margin-bottom: 40px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e8fff1;
}

.section-title i {
  color: #00c744;
}

.live-indicator {
  color: #dc3545 !important;
  font-size: 0.6rem;
  animation: pulse 1.5s ease-in-out infinite;
}

.live-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.live-card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.live-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #dc3545, #ff6b6b);
}

.live-card.upcoming::before {
  background: linear-gradient(90deg, #00c744, #00a83b);
}

.live-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 50px rgba(0, 199, 68, 0.15);
  border-color: rgba(0, 199, 68, 0.2);
}

.live-badge {
  display: inline-block;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 15px;
  animation: pulse 2s ease-in-out infinite;
}

.upcoming-badge {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%) !important;
  animation: none;
}

.live-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
  line-height: 1.4;
}

.live-course {
  font-size: 0.9rem;
  color: #718096;
  margin-bottom: 12px;
}

.live-status {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 15px;
  padding: 8px 12px;
  border-radius: 10px;
  display: inline-block;
}

.status-live {
  background: #fff5f5;
  color: #dc3545;
}

.status-upcoming {
  background: #f0fdf4;
  color: #00c744;
}

.status-ended {
  background: #f7fafc;
  color: #718096;
}

.join-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.25);
}

.join-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(0, 199, 68, 0.35);
}

.join-btn:disabled {
  background: #e2e8f0;
  color: #a0aec0;
  cursor: not-allowed;
  box-shadow: none;
}

/* ===== QBank Content ===== */
.qbank-content {
  animation: fadeInUp 0.4s ease;
}

.qbank-redirect {
  background: white;
  border-radius: 30px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  max-width: 500px;
  margin: 0 auto;
}

.qbank-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
}

.qbank-icon i {
  font-size: 45px;
  color: #00c744;
}

.qbank-redirect h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.qbank-redirect p {
  font-size: 1rem;
  color: #718096;
  margin-bottom: 25px;
}

.qbank-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 16px 35px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.qbank-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.4);
  color: white;
}

/* ===== Dark Mode for Tabs ===== */
[data-bs-theme="dark"] .tabs-wrapper,
[data-theme="dark"] .tabs-wrapper,
.dark .tabs-wrapper {
  background: #1e293b;
}

[data-bs-theme="dark"] .tab-btn,
[data-theme="dark"] .tab-btn,
.dark .tab-btn {
  color: #94a3b8;
}

[data-bs-theme="dark"] .tab-btn:hover,
[data-theme="dark"] .tab-btn:hover,
.dark .tab-btn:hover {
  background: #0f3d1c;
  color: #4ade80;
}

[data-bs-theme="dark"] .live-card,
[data-theme="dark"] .live-card,
.dark .live-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .live-title,
[data-theme="dark"] .live-title,
.dark .live-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .live-course,
[data-theme="dark"] .live-course,
.dark .live-course {
  color: #94a3b8;
}

[data-bs-theme="dark"] .section-title,
[data-theme="dark"] .section-title,
.dark .section-title {
  color: #f1f5f9;
  border-color: #334155;
}

[data-bs-theme="dark"] .qbank-redirect,
[data-theme="dark"] .qbank-redirect,
.dark .qbank-redirect {
  background: #1e293b;
}

[data-bs-theme="dark"] .qbank-redirect h3,
[data-theme="dark"] .qbank-redirect h3,
.dark .qbank-redirect h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .qbank-redirect p,
[data-theme="dark"] .qbank-redirect p,
.dark .qbank-redirect p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .qbank-icon,
[data-theme="dark"] .qbank-icon,
.dark .qbank-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .hero-section {
    padding: 40px 20px;
    border-radius: 0 0 30px 30px;
  }
  
  .hero-actions {
    top: 15px;
    left: 15px;
  }
  
  .action-btn {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  .hero-title {
    font-size: 1.8rem;
  }

  .hero-icon {
    width: 80px;
    height: 80px;
  }

  .hero-icon i {
    font-size: 35px;
  }

  .courses-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .stat-item {
    padding: 20px 25px;
  }

  .login-card {
    padding: 40px 30px;
  }

  .tabs-wrapper {
    gap: 5px;
    padding: 6px;
  }

  .tab-btn {
    padding: 10px 16px;
    font-size: 0.9rem;
  }

  .tab-btn span {
    display: none;
  }

  .tab-btn i {
    font-size: 1.2rem;
  }

  .live-cards {
    grid-template-columns: 1fr;
  }

  .qbank-redirect {
    padding: 40px 25px;
  }
}

/* QBank Subscriptions Section */
.qbank-subscriptions {
  padding: 20px 0;
}

/* Filter Section */
.filter-section {
  margin-bottom: 24px;
}

.filter-tabs {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.filter-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 25px;
  background: white;
  color: #6b7280;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-tab:hover {
  border-color: #00c444;
  color: #00c444;
}

.filter-tab.active {
  background: #00c444;
  border-color: #00c444;
  color: white !important;
}

.filter-tab.active i,
.filter-tab.active span {
  color: white !important;
}

.filter-tab i {
  font-size: 0.9rem;
}

/* New Subscription Section */
.new-subscription-section {
  margin-top: 30px;
  text-align: center;
}

.new-subscription-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #00c444 0%, #00a83c 100%);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}

.new-subscription-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.4);
  color: white;
  text-decoration: none;
}

.new-subscription-btn i {
  font-size: 1.1rem;
}

/* Dark Mode for QBank Section */
[data-bs-theme="dark"] .filter-tab,
.dark .filter-tab {
  background: #1f2937;
  border-color: #374151;
  color: #9ca3af;
}

[data-bs-theme="dark"] .filter-tab:hover,
.dark .filter-tab:hover {
  border-color: #00c444;
  color: #00c444;
}

[data-bs-theme="dark"] .filter-tab.active,
.dark .filter-tab.active {
  background: #00c444;
  border-color: #00c444;
  color: white;
}

/* Responsive */
@media (max-width: 768px) {
  .filter-tabs {
    gap: 8px;
  }

  .filter-tab {
    padding: 8px 16px;
    font-size: 0.85rem;
  }

  .new-subscription-btn {
    padding: 12px 24px;
    font-size: 0.9rem;
  }
}
</style>
